/**
 * Write-time validation for contextPath links (tags today, sub-projects when
 * they get a write tool).
 *
 * A contextPath is a repo-relative pointer agents auto-load. Before this, a
 * typo was only noticed at READ time, where `tag-context-resolver` maps a
 * missing path to a null summary and counts it in `stats.skipped` — the tag
 * looks fine in the UI while quietly delivering less context every session.
 *
 * The API server has no repo checkout, so the in-pod tools are the only write
 * surface that can see the tree. This module is that gate.
 */

import { readFile } from "node:fs/promises";
import { isAbsolute, join, normalize } from "node:path";
import { isPlaceholderLocator, locatorMatchesContent } from "@project/shared";
import { statWorkspacePath } from "../workbench/fs.js";

export interface ContextPathInput {
  type: string;
  path: string;
  label?: string;
  locator?: string;
  locatorType?: "test" | "code";
}

export type ContextPathProblemReason =
  | "not_found"
  | "expected_folder"
  | "expected_file"
  | "not_repo_relative"
  | "locator_not_found";

export interface ContextPathProblem {
  type: string;
  path: string;
  reason: ContextPathProblemReason;
  /** The locator that missed, on locator problems. */
  locator?: string;
}

const PROBLEM_TEXT: Record<ContextPathProblemReason, string> = {
  not_found: "does not exist in the repo",
  expected_folder: "is a file, not a folder — use type 'file', 'rule', or 'doc'",
  expected_file: "is a directory — use type 'folder'",
  not_repo_relative: "is not repo-relative (drop the leading '/' and any '..')",
  locator_not_found: "has a locator that does not match the file",
};

/** Don't read pathological files into memory just to check a locator. */
const MAX_LOCATOR_FILE_BYTES = 2 * 1024 * 1024;

/**
 * Check a link's locator against the real file. `null` = fine (matches, is a
 * placeholder, or the file could not be read — the async sweep owns judging
 * unreadable content); a problem = the locator demonstrably does not match.
 */
async function verifyLocator(
  link: ContextPathInput,
  absolutePath: string,
  fileSize: number | undefined,
): Promise<ContextPathProblem | null> {
  const { locator, locatorType } = link;
  if (!locator || !locatorType || isPlaceholderLocator(locator)) return null;
  if (fileSize !== undefined && fileSize > MAX_LOCATOR_FILE_BYTES) return null;
  let content: string;
  try {
    content = await readFile(absolutePath, "utf8");
  } catch {
    return null;
  }
  if (locatorMatchesContent(content, locatorType, locator)) return null;
  return { type: link.type, path: link.path, reason: "locator_not_found", locator };
}

/** Every link type resolves from the workspace: project docs are synced from
 *  the repo, so a 'doc' path is a workspace path too. Only 'folder' expects a
 *  directory; 'rule', 'doc', and 'file' all expect a regular file. */
function expectsDirectory(type: string): boolean {
  return type === "folder";
}

function classifyShape(rawPath: string): ContextPathProblemReason | "ok" {
  const trimmed = rawPath.trim();
  if (!trimmed) return "not_found";
  if (isAbsolute(trimmed)) return "not_repo_relative";
  const normalized = normalize(trimmed);
  if (normalized === ".." || normalized.startsWith("../")) return "not_repo_relative";
  return "ok";
}

/** Strip the noise agents commonly add around a repo-relative path. */
function toRelativePath(rawPath: string): string {
  return rawPath.trim().replace(/^\.\//, "").replace(/\/+$/, "");
}

/**
 * Stat every link against the workspace checkout and return the ones that do
 * not hold up. An empty array means every path is good.
 */
export async function verifyContextPaths(
  links: ContextPathInput[] | null | undefined,
  workspaceDir: string,
): Promise<ContextPathProblem[]> {
  if (!links?.length) return [];
  const problems: ContextPathProblem[] = [];

  for (const link of links) {
    const shape = classifyShape(link.path);
    if (shape !== "ok") {
      problems.push({ type: link.type, path: link.path, reason: shape });
      continue;
    }

    const absolutePath = join(workspaceDir, toRelativePath(link.path));
    const stat = await statWorkspacePath(absolutePath);
    const wantsDirectory = expectsDirectory(link.type);
    if (!stat.exists) {
      problems.push({ type: link.type, path: link.path, reason: "not_found" });
    } else if (wantsDirectory && !stat.isDirectory) {
      problems.push({ type: link.type, path: link.path, reason: "expected_folder" });
    } else if (!wantsDirectory && stat.isDirectory) {
      problems.push({ type: link.type, path: link.path, reason: "expected_file" });
    } else if (!wantsDirectory) {
      const locatorProblem = await verifyLocator(link, absolutePath, stat.size);
      if (locatorProblem) problems.push(locatorProblem);
    }
  }

  return problems;
}

/**
 * The rejection an agent reads. It names every bad path so the agent can fix
 * them all in one turn instead of retrying one at a time.
 */
export function formatContextPathProblems(problems: ContextPathProblem[]): string {
  const count = problems.length;
  return [
    `Rejected — ${count} contextPath${count === 1 ? "" : "s"} failed validation against the repo checkout:`,
    ...problems.map((p) => {
      const locator = p.locator === undefined ? "" : ` (locator "${p.locator}")`;
      return `- ${p.type} "${p.path}"${locator} ${PROBLEM_TEXT[p.reason]}`;
    }),
    'Nothing was written. contextPaths are repo-relative (e.g. "apps/api/src/services/tag"); a `test` locator must appear inside a real it/test/describe title in the file, a `code` locator anywhere in it. Check with ls/grep, then call the tool again.',
  ].join("\n");
}
